import { useMemo } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList, ReferenceLine } from 'recharts'
import useTranslation from '../../../../hooks/useTranslation'
import AnalyticsCard from './AnalyticsCard'
import { Headline, KpiRow, formatOf } from './Kpi'
import { ChartBox, TooltipCard, axisProps } from './ChartParts'
import { bucketLabel, spansYears } from './period'
import useAnalyticsWidget from './useAnalyticsWidget'
import useElementSize from './useElementSize'
import { chartFont } from '../../widgetTypography'

/*
 * One series in columns, one per bucket — counts per week, orders per month.
 *
 *     series     [{ id, label, value: <expression>, color, format }] — the first is drawn
 *     highlight  'last' — the current bucket in full colour, the rest softer;
 *                'none' (default) — all alike
 *     valueLabels  the value over each bar, when the bars are wide enough to carry it
 *     target     { value, label } — a reference line
 *
 * Every bar stands on a faint track the full height of the chart, so a bucket
 * with nothing in it is visibly an empty slot rather than a missing one.
 */
const VerticalBarWidget = ({ type, data: config, labels }) => {
    const { t } = useTranslation()
    const w = useAnalyticsWidget(type, config)
    const { period, data, palette, format, locale } = w
    const [ref, size] = useElementSize()

    const s = config.series?.[0] ?? {}
    const color = palette.color(s.color ?? 'primary', 0)
    const valueFormat = s.format ?? formatOf(s.value, config.measures)

    const rows = useMemo(() => {
        if (!data.current) return []
        const yearSpan = spansYears(period.range)
        const values = data.current.series(s.value)
        return period.keys.map((key, i) => ({ key, label: bucketLabel(key, period.grain, locale, { spansYears: yearSpan }), value: values[i] }))
    }, [data.current, s.value, period, locale])

    const compact = size.height < 120 || size.width < 220
    const showYAxis = !compact && size.width >= 340
    const barWidth = rows.length ? size.width / rows.length : 0
    const showLabels = config.valueLabels && !compact && barWidth >= 34

    const renderTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null
        const row = payload[0].payload
        return <TooltipCard title={bucketLabel(row.key, period.grain, locale, { long: true })}
            rows={[{ color, name: t(s.label), value: format(row.value, valueFormat) }]} />
    }

    return (
        <AnalyticsCard
            labels={labels}
            period={period}
            status={w.status}
            error={data.error}
            onRetry={data.refetch}
            headline={<Headline kpi={w.headline} formatValue={format} compareLabel={w.compareLabel} />}
            footer={<KpiRow kpis={w.kpis} formatValue={format} />}
        >
            <ChartBox>
                <div ref={ref}>
                    {size.width > 0 && size.height > 0 && (
                        <BarChart width={size.width} height={size.height} data={rows} accessibilityLayer
                            barCategoryGap={rows.length > 20 ? '18%' : '28%'}
                            margin={{ top: showLabels ? 18 : 6, right: 4, left: showYAxis ? 0 : 4, bottom: 0 }}>
                            {!compact && <CartesianGrid vertical={false} stroke={palette.grid} strokeDasharray="3 3" />}
                            <XAxis dataKey="label" hide={compact && size.height < 80} {...axisProps(palette)} minTickGap={12} interval="preserveStartEnd" />
                            <YAxis hide={!showYAxis} width={48} {...axisProps(palette)} allowDecimals={valueFormat !== 'integer'}
                                tickFormatter={v => format(v, valueFormat, { compact: true })} />
                            <Tooltip content={renderTooltip} cursor={{ fill: palette.track }} />
                            {config.target && (
                                <ReferenceLine y={config.target.value} stroke={palette.tokens.neutral} strokeDasharray="4 4"
                                    label={{ value: t(config.target.label), position: 'insideTopRight', fontSize: chartFont.label, fill: palette.axis }} />
                            )}
                            <Bar dataKey="value" name={t(s.label)} radius={[4, 4, 0, 0]} background={{ fill: palette.track, radius: 4 }}
                                isAnimationActive={false} maxBarSize={56}>
                                {rows.map((row, i) => (
                                    <Cell key={row.key} fill={color}
                                        fillOpacity={config.highlight === 'last' && i !== rows.length - 1 ? 0.45 : 1} />
                                ))}
                                {showLabels && (
                                    <LabelList dataKey="value" position="top" fontSize={chartFont.label} fill={palette.axis}
                                        formatter={v => format(v, valueFormat, { compact: true })} />
                                )}
                            </Bar>
                        </BarChart>
                    )}
                </div>
            </ChartBox>
        </AnalyticsCard>
    )
}

export default VerticalBarWidget
